/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
import MotionPathPlugin from "gsap/MotionPathPlugin";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText, MotionPathPlugin);

import Swiper from "swiper";
import {
	Autoplay,
	// EffectFade,
	FreeMode,
	Navigation,
	Pagination,
	Scrollbar,
	// Thumbs,
} from "swiper/modules";

import { Application } from "@splinetool/runtime";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'page-no-js' body class
	document.body.classList.remove("page-no-js");
	gsapPreloader();
	scrollDetection(10);
	menuBurger();
	menuDropdown();
	bannerInit();
	bannerToMarquee();
	dataSrc();
	dataPopin();
});

window.addEventListener("load", () => {
	// splineInit();
	setTimeout(() => splineInit(), 900);
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurger = document.querySelector("#page-header-burger");
	const headerBackdrop = document.querySelector("#page-header-backdrop");

	if (headerBurger) {
		headerBurger.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
				gsapMenuBurger();
			}
		});
	}

	if (headerBackdrop) {
		headerBackdrop.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const bannerInit = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Convertir les [data-src] en [src]
const dataSrc = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Gestions des actions des [data-popin]
const dataPopin = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	// .swiper[data-swiper]
	const swiperListeners = document.querySelectorAll(".swiper[data-swiper]");
	swiperListeners.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 1) {
			// Lire le type de swiper défini dans l'attribut data-swiper
			const swiperType = swiper.getAttribute("data-swiper");

			// Configuration de base
			const config = {
				slidesPerView: "auto",
				speed: 900,
				resizeObserver: true,
				modules: [Autoplay, FreeMode, Navigation, Pagination, Scrollbar],
				navigation: {
					prevEl: swiper.querySelector(".swiper-navigation-prev"),
					nextEl: swiper.querySelector(".swiper-navigation-next"),
					disabledClass: "--disabled",
					lockClass: "--hidden",
				},
				pagination: {
					el: swiper.querySelector(".swiper-pagination"),
					clickable: true,
					hiddenClass: "--hidden",
				},
				scrollbar: {
					el: swiper.querySelector(".swiper-scrollbar"),
					draggable: true,
					hide: true,
				},
			};

			// Ajouter les options conditionnelles
			if (swiperType === "autoplay") {
				config.autoplay = { delay: 3600, disableOnInteraction: true };
			}

			if (swiperType === "free") {
				config.freeMode = true;
			}

			// Initialisation du Swiper
			new Swiper(swiper, config);
		}
	});
};

/* -------------------------------- (SPLINE) -------------------------------- */

// Spline : Initialisation des zones Spline
const splineInit = () => {
	// cherche toutes les zones avec l’attribut data-spline
	const splineAreas = document.querySelectorAll("[data-spline]");

	if (splineAreas.length > 0) {
		splineAreas.forEach((area) => {
			if (area.dataset.spline === "") return;

			// Crée un canvas dans chaque zone
			const canvas = document.createElement("canvas");
			canvas.style.width = "100%";
			canvas.style.height = "100%";
			area.appendChild(canvas);

			// Correction du blocage de scroll
			canvas.addEventListener(
				"wheel",
				(e) => {
					e.stopImmediatePropagation();
				},
				{ passive: true, capture: true }
			);

			// charge la scène Spline dans le canvas
			const spline = new Application(canvas);
			spline.load(
				"https://prod.spline.design/" +
					area.dataset.spline +
					"/scene.splinecode"
			);
		});
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Animations liés au menuBurger
const gsapMenuBurger = () => {
	const burgerTimeline = gsap.timeline();
	burgerTimeline
		.from(["#page-header .header__fullmenu__media"], {
			delay: 0.3,
			duration: 0.6,
			ease: "power2.out",
			autoAlpha: 0,
			x: 40,
		})
		.from(
			["#page-header .header__fullmenu__menu > *"],
			{
				duration: 0.6,
				stagger: 0.05,
				ease: "power2.out",
				autoAlpha: 0,
				x: 40,
			},
			"<.1"
		)
		.from(
			["#page-header .header__fullmenu__actions"],
			{
				duration: 0.6,
				ease: "power2.out",
				autoAlpha: 0,
				x: 40,
			},
			"<.3"
		)
		.from(
			["#page-header .header__fullmenu__more > *"],
			{
				duration: 0.6,
				stagger: 0.05,
				ease: "power2.out",
				autoAlpha: 0,
				x: 40,
			},
			"<.1"
		);
};

// GSAP : Elements sticky (avec ScrollTrigger)
const gsapStickySidebar = () => {
	// On récupère tous les éléments comportant l'attribut data-sticky
	const stickyElements = document.querySelectorAll("[data-sticky]");

	stickyElements.forEach((sticky) => {
		const parent = sticky.parentElement;
		if (parent && parent.offsetHeight > sticky.offsetHeight) {
			const defaultTop = document.querySelector("#page-header").offsetHeight
				? document.querySelector("#page-header").offsetHeight + 30
				: 150;
			const topValue = sticky.dataset.sticky
				? parseInt(sticky.dataset.sticky, 10)
				: defaultTop;
			const topPx = isNaN(topValue) ? defaultTop : topValue;
			// console.log(topPx);
			ScrollTrigger.create({
				trigger: sticky,
				start: `-${topPx}px top`,
				end: `+=${parent.offsetHeight - sticky.offsetHeight}`,
				pin: true,
				pinSpacing: false,
				// markers: true,
			});
		}
	});
};

// GSAP : Détecte les sections avec des #ancre visibles (avec ScrollTrigger)
const gsapAnchorsDetection = () => {
	// Sélectionner tous les liens avec une ancre
	const anchorLinks = document.querySelectorAll(
		'.section__summary__link[href^="#"]'
	);
	anchorLinks.forEach((link) => {
		const targetId = link.getAttribute("href");

		// Vérifier que l'ID n'est pas juste un "#"
		if (targetId !== "#") {
			const targetSection = document.querySelector(targetId);

			if (targetSection) {
				// Ajouter des écouteurs ScrollTrigger aux sections
				ScrollTrigger.create({
					trigger: targetSection,
					start: "top 50%",
					end: "bottom 50%",
					onEnter: () => {
						link.setAttribute("aria-current", "true");
					},
					onLeave: () => {
						link.removeAttribute("aria-current");
					},
					onEnterBack: () => {
						link.setAttribute("aria-current", "true");
					},
					onLeaveBack: () => {
						link.removeAttribute("aria-current");
					},
					// markers: true,
				});
			} else {
				link.parentNode.classList.add("--hidden");
			}
		}
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapDataAnimation = () => {
	// [data-animation] + .elementor-element > .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"#page-container [data-animation]",
		"#page-container .elementor-heading-title",
		"#page-container .elementor-element:not([data-animation]) > .elementor-element",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="reveal-text"
	const textsToAnimated = document.querySelectorAll([
		// global
		'#page-container [data-animation="reveal-text"]',
		"#page-container .elementor-element:not([data-animation]) > .elementor-widget-container > .elementor-heading-title",
	]);
	textsToAnimated.forEach((_thisText) => {
		const delay = _thisText.getAttribute("data-animation-delay")
			? parseInt(_thisText.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		const split = SplitText.create(_thisText, {
			type: "lines, words",
			autoSplit: true,
			mask: "lines",
			onSplit(self) {
				// Ajout d'une classe et d'un style de sécurité à chaque ligne
				self.lines.forEach((line) => {
					line.style.marginBottom = "-0.1em";
					line.style.paddingBottom = "0.1em";
				});
				gsap.from(self.words, {
					scrollTrigger: {
						trigger: _thisText,
						start: "top 90%",
						toggleActions: "play none none reverse",
						// markers: true,
					},
					yPercent: 100, // recommandé pour éviter les bugs de clipping
					autoAlpha: 0,
					delay: delay,
					duration: 0.8,
					ease: "power3.out",
					stagger: 0.05,
				});
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll(
		'[data-animation="reveal-text-reading"]'
	);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;

		const split = SplitText.create(_this, {
			type: "words, chars",
			onSplit(self) {
				gsap.fromTo(
					self.chars,
					{
						opacity: 0.1,
					},
					{
						opacity: 1,
						delay: delay,
						stagger: 0.4,
						ease: "power1.out",
						scrollTrigger: {
							trigger: _this,
							start: "top 90%",
							end: "top 20%",
							scrub: true,
							// markers: true
						},
					}
				);
			},
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]'
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="clip"
	const elementsToClip = document.querySelectorAll('[data-animation="clip"]');
	elementsToClip.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		gsap.fromTo(
			_this,
			{
				clipPath: "inset(25% round 2.5em)",
			},
			{
				clipPath: "inset(0% round 2.5em)",
				ease: "power1.out",
				scrollTrigger: {
					trigger: _this,
					start: "top 100%",
					end: "bottom 50%",
					scrub: true,
					// markers: true,
				},
			}
		);
	});
};

// GSAP : Animation d'horloge
const gsapBatteryAnimation = () => {
	/* ------------------------- Animations liées au svg ------------------------ */
	const [line, marker, label1, label2] = [
		'[data-name="line"]',
		'[data-name="marker"]',
		'[data-name="label-1"]',
		'[data-name="label-2"]',
	].map((sel) => document.querySelector(sel));

	if (line && marker && label1 && label2) {
		// Durée totale
		const totalDuration = 5;

		// Masquer labels au départ
		gsap.set([label1, label2], { opacity: 0 });

		const tl = gsap.timeline({
			repeat: -1,
			defaults: { ease: "power2.inOut" },
		});

		// Déplacement du marker
		tl.to(
			marker,
			{
				duration: totalDuration,
				motionPath: {
					path: line,
					align: line,
					alignOrigin: [0.5, 0.5],
					// autoRotate: true
				},
			},
			0
		);

		// Changement la couleur du marker
		tl.to(
			marker.querySelector("path:last-child"),
			{
				stroke: "var(--color-success)",
				duration: totalDuration * 0.33,
			},
			totalDuration * 0.33
		);

		// Labels
		tl.fromTo(
			label1,
			{
				y: 5,
			},
			{
				y: 0,
				opacity: 1,
				duration: totalDuration * 0.1,
			},
			totalDuration * 0
		);
		tl.to(
			label1,
			{
				opacity: 0,
				duration: totalDuration * 0.1,
			},
			totalDuration * 0.45
		);

		tl.fromTo(
			label2,
			{
				y: 5,
			},
			{
				opacity: 1,
				y: 0,
				duration: totalDuration * 0.1,
			},
			totalDuration * 0.45
		);
		tl.to(
			label2,
			{
				opacity: 0,
				duration: totalDuration * 0.1,
			},
			totalDuration * 0.9
		);

		// Faire disparaître le marker à la fin
		tl.to(
			marker,
			{ opacity: 0, duration: totalDuration * 0.1 },
			totalDuration * 0.9
		);
	}

	/* ----------------------- Animations liés à la vidéo ----------------------- */
	const duration = 12;
	const battery = document.querySelector(".battery");
	if (!battery) return;

	// (Animations synchronisées à la vidéo)
	const playAnimation = () => {
		// (affecter à la variables CSS)
		const playingElement = document.querySelector("[data-playing]");
		if (playingElement) {
			playingElement.style.setProperty("--duration", `${duration}s`);
		}

		// (lancer les animations CSS de [data-playing])
		const player = document.querySelector("[data-playing]");
		if (player) player.dataset.playing = "true";

		// (lancer les animations CSS de [data-charging])
		const charging = document.querySelector("[data-charging]");
		if (charging) {
			let isCharging = charging.dataset.charging === "true";
			const switchDelay = (duration / 2) * 1000; // en ms

			setInterval(() => {
				isCharging = !isCharging;
				charging.dataset.charging = isCharging ? "true" : "false";
			}, switchDelay);
		}

		// (affichage du timer)
		const timer = battery.querySelector("[data-timer]");
		if (timer) {
			const hourEl = timer.querySelector(".timer__hour");
			const minuteEl = timer.querySelector(".timer__minute");

			const startHour = 23;
			const startMinute = 0;

			// Nombre total de slices : (15min)
			const totalSlices = 24 * 4;
			const startSlices = startHour * 4 + startMinute / 15;

			// Timeline GSAP pour le timer
			const tl = gsap.timeline({ repeat: -1 });

			tl.to(
				{},
				{
					duration: duration,
					ease: "none",
					onUpdate: function () {
						let progress = this.progress(); // 0 à 1

						// Ajouter le décalage initial
						const timeSlices =
							Math.floor(progress * totalSlices + startSlices) % totalSlices;

						const hours = Math.floor(timeSlices / 4) % 24;
						const minutes = (timeSlices % 4) * 15;

						hourEl.textContent = hours.toString().padStart(2, "0");
						minuteEl.textContent = minutes.toString().padStart(2, "0");
					},
				}
			);
		}
	};

	// (lancement de la vidéo)
	const video = battery.querySelector("video");
	if (video) {
		// Activer la lecture en boucle
		video.loop = true;

		// Essayer de lancer la vidéo
		const playPromise = video.play();

		// Certains navigateurs demandent un traitement des promesses
		if (playPromise !== undefined) {
			playPromise
				.then(() => {
					playAnimation();
				})
				.catch((error) => {
					console.warn("Lecture automatique échouée :", error);
				});
		}
	}
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	gsapDataAnimation();
	gsapBatteryAnimation();
	swiperInit();
	gsapStickySidebar();
	gsapAnchorsDetection();
	scrollToAnchor();
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	loaderTimeline
		// animation
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			autoAlpha: 0,
		})
		.set("#page-preloader", {
			autoAlpha: 0,
		});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
